/**
 * V4.21 — Dashboard de Importação de Boletos (admin)
 * /v2/financeiro/dnapay/importar-boletos/dashboard
 * Lista histórico de lotes, permite ver detalhes, baixar PDFs e reverter importações.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../lib/api';
import { toast } from 'sonner';
import { RefreshCw, Eye, RotateCcw, FileText, CheckCircle2, XCircle, Clock, Upload, Download, Trash2, Sparkles, Link2 } from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

const STATUS = {
  match_ok:   { label: 'Match OK',   cls: 'dna-tone-success' },
  sem_aluno:  { label: 'Sem aluno',  cls: 'dna-tone-warning' },
  sem_titulo: { label: 'Sem título', cls: 'dna-tone-warning' },
  duplicado:  { label: 'Duplicado',  cls: 'dna-tone-neutral' },
  invalido:   { label: 'Inválido',   cls: 'dna-tone-danger' },
};

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString('pt-BR') : '—';
const fmtBRL = (c) => c == null ? '—' : (Number(c)/100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

function BatchStatusBadge({ b }) {
  if (b.reverted) return <span data-testid={`status-badge-${b.id}`} className="px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-red-50 text-red-700 border-red-200 inline-flex items-center gap-1"><XCircle className="w-3 h-3"/>Revertido</span>;
  if (b.confirmed) return <span data-testid={`status-badge-${b.id}`} className="px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-emerald-50 text-emerald-700 border-emerald-200 inline-flex items-center gap-1"><CheckCircle2 className="w-3 h-3"/>Confirmado</span>;
  return <span data-testid={`status-badge-${b.id}`} className="px-2 py-0.5 rounded-full text-[11px] font-semibold border bg-amber-50 text-amber-700 border-amber-200 inline-flex items-center gap-1"><Clock className="w-3 h-3"/>Pendente</span>;
}

function DetailsModal({ batchId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get(`/financeiro/boletos-import/${batchId}/preview`);
        if (!cancel) setData(data);
      } catch (e) {
        toast.error('Falha ao carregar detalhes do lote');
        onClose();
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [batchId, onClose]);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="details-modal">
      <div className="bg-white rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="text-sm text-slate-500">Lote</div>
            <div className="font-mono text-xs text-slate-700">{batchId}</div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900 text-2xl leading-none" data-testid="close-details">×</button>
        </div>
        {loading ? (
          <div className="p-10 text-center text-slate-500"><RefreshCw className="w-5 h-5 animate-spin inline mr-2"/>Carregando…</div>
        ) : (
          <div className="overflow-auto flex-1">
            <table className="min-w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-[11px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Banco</th>
                  <th className="px-3 py-2 text-left">Nosso Nº</th>
                  <th className="px-3 py-2 text-left">Aluno</th>
                  <th className="px-3 py-2 text-right">Valor</th>
                  <th className="px-3 py-2 text-center">Vencimento</th>
                  <th className="px-3 py-2 text-center">PDF</th>
                </tr>
              </thead>
              <tbody>
                {(data?.items || []).map((it) => (
                  <tr key={it.item_id} className="border-b border-slate-100">
                    <td className="px-3 py-2"><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${STATUS[it.status_match]?.cls}`}>{STATUS[it.status_match]?.label || it.status_match}</span></td>
                    <td className="px-3 py-2">{it.banco_nome || '—'}</td>
                    <td className="px-3 py-2 font-mono text-xs">{it.nosso_numero || '—'}</td>
                    <td className="px-3 py-2">{it.student_name || it.aluno_nome || '—'}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold">{fmtBRL(it.valor_cents)}</td>
                    <td className="px-3 py-2 text-center whitespace-nowrap">{it.vencimento ? new Date(it.vencimento + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}</td>
                    <td className="px-3 py-2 text-center">
                      <a href={`${API_BASE}/financeiro/boletos-import/pdf/${it.file_id}.pdf`} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:underline text-xs" data-testid={`modal-view-${it.item_id}`}>Ver</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ImportarBoletosDashboard() {
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsFor, setDetailsFor] = useState(null);
  const [reverting, setReverting] = useState(null);
  const [confirmRevert, setConfirmRevert] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [orphansPreview, setOrphansPreview] = useState(null);
  const [cleanupRunning, setCleanupRunning] = useState(false);
  const [reconcileReport, setReconcileReport] = useState(null);
  const [reconcileRunning, setReconcileRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/financeiro/boletos-import/batches');
      setBatches(data.batches || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao carregar lotes');
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doRevert = async (batch) => {
    setReverting(batch.id);
    try {
      const { data } = await api.post(`/financeiro/boletos-import/batches/${batch.id}/revert`);
      toast.success(`Lote revertido — ${data.removed} registros removidos`);
      setConfirmRevert(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao reverter lote');
    } finally { setReverting(null); }
  };

  const doDelete = async (batch) => {
    setReverting(batch.id);
    try {
      await api.delete(`/financeiro/boletos-import/batches/${batch.id}`, { data: { reason: 'excluido_pelo_admin' } });
      toast.success('Lote excluído');
      setConfirmDelete(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao excluir lote');
    } finally { setReverting(null); }
  };

  const openOrphans = async () => {
    try {
      const { data } = await api.get('/financeiro/boletos-import/orphans');
      setOrphansPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha ao consultar órfãos');
    }
  };

  const doCleanup = async () => {
    setCleanupRunning(true);
    try {
      const { data } = await api.post('/financeiro/boletos-import/orphans/cleanup');
      toast.success(`Limpeza concluída — ${data.removed} órfão(s) removido(s)${data.preserved ? ` · ${data.preserved} preservado(s) por conter pagamento` : ''}`);
      setOrphansPreview(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha na limpeza');
    } finally { setCleanupRunning(false); }
  };

  const runReconcile = async (dry) => {
    setReconcileRunning(true);
    try {
      const { data } = await api.post(`/financeiro/boletos-import/reconcile-links?dry_run=${dry}`);
      setReconcileReport(data);
      if (!dry) toast.success(`Reconcile aplicado — ${data.would_create_title} título(s) criado(s), ${data.would_link_existing_title} vinculado(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Falha no reconcile');
    } finally { setReconcileRunning(false); }
  };

  const totals = useMemo(() => {
    return batches.reduce((acc, b) => {
      acc.total += 1;
      if (b.confirmed && !b.reverted) acc.ativos += 1;
      if (b.reverted) acc.revertidos += 1;
      if (!b.confirmed) acc.pendentes += 1;
      acc.dnapay += b.n_active_now || 0;
      return acc;
    }, { total: 0, ativos: 0, revertidos: 0, pendentes: 0, dnapay: 0 });
  }, [batches]);

  return (
    <div className="p-6 max-w-[1400px] mx-auto" data-testid="importar-boletos-dashboard">
      <div className="mb-5 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center"><FileText className="w-5 h-5" /></div>
        <div className="flex-1">
          <h1 className="text-2xl font-extrabold tracking-tight text-slate-900">Dashboard de Importação de Boletos</h1>
          <p className="text-sm text-slate-500">Histórico completo de lotes · reverter ou consultar detalhes.</p>
        </div>
        <Link to="/v2/financeiro/dnapay/importar-boletos" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold" data-testid="new-import-btn">
          <Upload className="w-4 h-4" /> Nova importação
        </Link>
        <button onClick={openOrphans} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-sm font-semibold" data-testid="cleanup-orphans-btn" title="Remover boletos residuais de importações antigas">
          <Sparkles className="w-4 h-4" /> Limpar órfãos
        </button>
        <button onClick={() => runReconcile(true)} disabled={reconcileRunning} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-sm font-semibold disabled:opacity-50" data-testid="reconcile-btn" title="Vincular boletos importados a alunos, responsáveis e títulos oficiais">
          {reconcileRunning ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Link2 className="w-4 h-4"/>} Reconciliar títulos
        </button>
        <button onClick={load} className="p-2 rounded-lg border border-slate-200 hover:bg-slate-50" data-testid="refresh-btn"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /></button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="kpi-total"><div className="text-xs text-slate-500">Total de lotes</div><div className="text-2xl font-bold">{totals.total}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="kpi-ativos"><div className="text-xs text-slate-500">Ativos</div><div className="text-2xl font-bold text-emerald-700">{totals.ativos}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="kpi-pendentes"><div className="text-xs text-slate-500">Pendentes</div><div className="text-2xl font-bold text-amber-700">{totals.pendentes}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="kpi-revertidos"><div className="text-xs text-slate-500">Revertidos</div><div className="text-2xl font-bold text-red-700">{totals.revertidos}</div></div>
        <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid="kpi-dnapay"><div className="text-xs text-slate-500">Boletos ativos no DNAPay</div><div className="text-2xl font-bold text-indigo-700">{totals.dnapay}</div></div>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[11px] uppercase tracking-wider text-slate-500">
                <th className="px-3 py-2 text-left">Data</th>
                <th className="px-3 py-2 text-left">Importado por</th>
                <th className="px-3 py-2 text-center">Arquivos</th>
                <th className="px-3 py-2 text-center">Itens</th>
                <th className="px-3 py-2 text-left">Contagens</th>
                <th className="px-3 py-2 text-center">Ativos agora</th>
                <th className="px-3 py-2 text-center">Status</th>
                <th className="px-3 py-2 text-center">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan="8" className="text-center py-8 text-slate-500"><RefreshCw className="w-5 h-5 animate-spin inline mr-2"/>Carregando lotes…</td></tr>
              )}
              {!loading && batches.length === 0 && (
                <tr><td colSpan="8" className="text-center py-10 text-slate-500" data-testid="empty-state">Nenhuma importação ainda. Comece em <Link to="/v2/financeiro/dnapay/importar-boletos" className="text-indigo-600 hover:underline">Nova importação</Link>.</td></tr>
              )}
              {!loading && batches.map((b, idx) => (
                <tr key={b.id} className={idx % 2 ? 'bg-slate-50/60' : ''} data-testid={`batch-row-${b.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap">{fmtDT(b.created_at)}</td>
                  <td className="px-3 py-2">{b.created_by_name || '—'}</td>
                  <td className="px-3 py-2 text-center">{b.n_files}</td>
                  <td className="px-3 py-2 text-center">{b.n_items}</td>
                  <td className="px-3 py-2">
                    <div className="flex flex-wrap gap-1">
                      {Object.entries(b.counts || {}).filter(([, v]) => v > 0).map(([k, v]) => (
                        <span key={k} className={`px-1.5 py-0.5 rounded text-[10px] font-semibold border ${STATUS[k]?.cls}`}>{STATUS[k]?.label || k}: {v}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-center tabular-nums">
                    <span className="font-semibold text-indigo-700">{b.n_active_now}</span>
                    <span className="text-slate-400 text-xs"> / {b.n_inserted} inseridos</span>
                  </td>
                  <td className="px-3 py-2 text-center"><BatchStatusBadge b={b} /></td>
                  <td className="px-3 py-2">
                    <div className="flex items-center justify-center gap-1.5">
                      <button onClick={() => setDetailsFor(b.id)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-50 hover:bg-slate-100 border border-slate-200 text-[11px]" data-testid={`view-${b.id}`}><Eye className="w-3 h-3" /> Ver</button>
                      {(b.files || []).length > 0 && (
                        <a href={`${API_BASE}/financeiro/boletos-import/pdf/${b.files[0].file_id}.pdf`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-2 py-1 rounded bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 text-indigo-700 text-[11px]" data-testid={`dl-${b.id}`} title={`Baixar ${b.files.length} PDF(s)`}><Download className="w-3 h-3" /> PDF</a>
                      )}
                      {b.confirmed && !b.reverted && (
                        <button onClick={() => setConfirmRevert(b)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 text-[11px]" data-testid={`revert-${b.id}`}><RotateCcw className="w-3 h-3" /> Reverter</button>
                      )}
                      {(!b.confirmed || b.reverted) && (
                        <button onClick={() => setConfirmDelete(b)} className="inline-flex items-center gap-1 px-2 py-1 rounded bg-red-50 hover:bg-red-100 border border-red-200 text-red-700 text-[11px]" data-testid={`delete-${b.id}`}><Trash2 className="w-3 h-3" /> Excluir</button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {detailsFor && <DetailsModal batchId={detailsFor} onClose={() => setDetailsFor(null)} />}

      {confirmRevert && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-revert-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Reverter esta importação?</h3>
            <p className="text-sm text-slate-600 mb-4">
              Isso irá remover <b>{confirmRevert.n_active_now}</b> boleto(s) atualmente ativos no DNAPay que foram <b>criados</b> por esta importação.
              Boletos que já existiam antes e apenas foram atualizados/vinculados <b>não</b> serão removidos.
              Esta ação será registrada no histórico.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmRevert(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50" data-testid="cancel-revert">Cancelar</button>
              <button onClick={() => doRevert(confirmRevert)} disabled={!!reverting} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2" data-testid="confirm-revert-btn">
                {reverting ? <RefreshCw className="w-4 h-4 animate-spin"/> : <RotateCcw className="w-4 h-4"/>}
                Confirmar reversão
              </button>
            </div>
          </div>
        </div>
      )}

      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="confirm-delete-modal">
          <div className="bg-white rounded-2xl max-w-md w-full p-6">
            <h3 className="text-lg font-bold text-slate-900 mb-2">Excluir este lote?</h3>
            <p className="text-sm text-slate-600 mb-4">
              O lote será removido do histórico. Se ele foi confirmado, precisa estar <b>revertido</b> primeiro.
              Se houver título com pagamento registrado, a exclusão é bloqueada.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmDelete(null)} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50" data-testid="cancel-delete">Cancelar</button>
              <button onClick={() => doDelete(confirmDelete)} disabled={!!reverting} className="px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2" data-testid="confirm-delete-btn">
                {reverting ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Trash2 className="w-4 h-4"/>}
                Confirmar exclusão
              </button>
            </div>
          </div>
        </div>
      )}

      {orphansPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !cleanupRunning && setOrphansPreview(null)} data-testid="orphans-modal">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-2 inline-flex items-center gap-2"><Sparkles className="w-5 h-5 text-amber-600"/>Boletos órfãos encontrados</h3>
            <p className="text-sm text-slate-600 mb-3">
              São registros em <code className="text-xs bg-slate-100 px-1 rounded">dnapay_titulos</code> com <code className="text-xs bg-slate-100 px-1 rounded">source=boleto_importado</code> cujo lote de origem não existe mais (resíduos de importações antigas ou reverts incompletos). Eles fazem novos PDFs baterem como <b>&quot;Duplicado&quot;</b>.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 text-sm">
              <div className="flex justify-between mb-1">
                <span className="text-slate-600">Total encontrado:</span>
                <span className="font-bold text-slate-900" data-testid="orphans-total">{orphansPreview.total}</span>
              </div>
              <div className="flex justify-between mb-1">
                <span className="text-slate-600">Serão removidos:</span>
                <span className="font-bold text-red-700">{orphansPreview.total - orphansPreview.protected}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Preservados (com pagamento):</span>
                <span className="font-bold text-emerald-700">{orphansPreview.protected}</span>
              </div>
            </div>
            {orphansPreview.orphans.length > 0 && (
              <div className="max-h-48 overflow-auto border border-slate-200 rounded-lg mb-4">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 sticky top-0"><tr className="text-[10px] uppercase text-slate-500"><th className="px-2 py-1 text-left">Nosso Nº</th><th className="px-2 py-1 text-right">Valor</th><th className="px-2 py-1 text-center">Venc</th><th className="px-2 py-1 text-center">Status</th></tr></thead>
                  <tbody>
                    {orphansPreview.orphans.slice(0, 100).map((o) => (
                      <tr key={o.id} className="border-t border-slate-100">
                        <td className="px-2 py-1 font-mono">{o.nosso_numero || '—'}</td>
                        <td className="px-2 py-1 text-right tabular-nums">{o.amount_cents != null ? (o.amount_cents/100).toLocaleString('pt-BR',{style:'currency',currency:'BRL'}) : '—'}</td>
                        <td className="px-2 py-1 text-center">{o.due_date || '—'}</td>
                        <td className="px-2 py-1 text-center">{o.protected ? <span className="text-emerald-700">🔒 preservar</span> : <span className="text-red-700">remover</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="flex gap-2 justify-end">
              <button onClick={() => setOrphansPreview(null)} disabled={cleanupRunning} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50" data-testid="cancel-cleanup">Cancelar</button>
              <button onClick={doCleanup} disabled={cleanupRunning || orphansPreview.total === 0} className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2" data-testid="confirm-cleanup-btn">
                {cleanupRunning ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Sparkles className="w-4 h-4"/>}
                Limpar {orphansPreview.total - orphansPreview.protected} órfão(s)
              </button>
            </div>
          </div>
        </div>
      )}

      {reconcileReport && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => !reconcileRunning && setReconcileReport(null)} data-testid="reconcile-modal">
          <div className="bg-white rounded-2xl max-w-3xl w-full p-6 max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-slate-900 mb-2 inline-flex items-center gap-2"><Link2 className="w-5 h-5 text-emerald-600"/>Reconcile — {reconcileReport.dry_run ? 'Prévia (dry-run)' : 'Aplicado'}</h3>
            <p className="text-sm text-slate-600 mb-3">
              Vincula boletos importados a alunos, responsáveis e títulos oficiais em <code className="text-xs bg-slate-100 px-1 rounded">db.titles</code>. Não emite boleto novo. Não duplica.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-sm mb-4">
              <div className="bg-slate-50 border border-slate-200 rounded p-2"><div className="text-slate-500 text-xs">Examinados</div><div className="font-bold text-slate-900">{reconcileReport.examined}</div></div>
              <div className="bg-emerald-50 border border-emerald-200 rounded p-2"><div className="text-slate-500 text-xs">Já vinculados</div><div className="font-bold text-emerald-800">{reconcileReport.already_linked}</div></div>
              <div className="bg-blue-50 border border-blue-200 rounded p-2"><div className="text-slate-500 text-xs">Vincular a title existente</div><div className="font-bold text-blue-800">{reconcileReport.would_link_existing_title}</div></div>
              <div className="bg-indigo-50 border border-indigo-200 rounded p-2"><div className="text-slate-500 text-xs">Criar novo title</div><div className="font-bold text-indigo-800">{reconcileReport.would_create_title}</div></div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2"><div className="text-slate-500 text-xs">Vincular aluno</div><div className="font-bold text-amber-800">{reconcileReport.would_link_student}</div></div>
              <div className="bg-amber-50 border border-amber-200 rounded p-2"><div className="text-slate-500 text-xs">Vincular responsável</div><div className="font-bold text-amber-800">{reconcileReport.would_link_responsible}</div></div>
              <div className="bg-red-50 border border-red-200 rounded p-2"><div className="text-slate-500 text-xs">Sem aluno</div><div className="font-bold text-red-800">{reconcileReport.missing_student}</div></div>
              <div className="bg-red-50 border border-red-200 rounded p-2"><div className="text-slate-500 text-xs">Sem responsável</div><div className="font-bold text-red-800">{reconcileReport.missing_responsible}</div></div>
              <div className="bg-slate-50 border border-slate-200 rounded p-2"><div className="text-slate-500 text-xs">Erros</div><div className="font-bold text-red-700">{reconcileReport.errors}</div></div>
            </div>
            {reconcileReport.samples?.length > 0 && (
              <div className="mb-4">
                <div className="text-xs uppercase text-slate-500 mb-1">Amostra ({reconcileReport.samples.length} primeiros)</div>
                <div className="max-h-48 overflow-auto border border-slate-200 rounded">
                  <table className="min-w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0"><tr className="text-[10px] uppercase text-slate-500"><th className="px-2 py-1 text-left">Nosso Nº</th><th className="px-2 py-1 text-right">Valor</th><th className="px-2 py-1 text-center">Venc</th><th className="px-2 py-1 text-center">Aluno</th><th className="px-2 py-1 text-center">Resp</th><th className="px-2 py-1 text-center">Title</th><th className="px-2 py-1 text-left">Ação</th></tr></thead>
                    <tbody>
                      {reconcileReport.samples.map((s, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-2 py-1 font-mono">{s.nosso_numero || '—'}</td>
                          <td className="px-2 py-1 text-right tabular-nums">{s.amount_cents != null ? (s.amount_cents/100).toLocaleString('pt-BR',{style:'currency',currency:'BRL'}) : '—'}</td>
                          <td className="px-2 py-1 text-center">{s.due_date || '—'}</td>
                          <td className="px-2 py-1 text-center">{s.student_found ? '✅' : '❌'}</td>
                          <td className="px-2 py-1 text-center">{s.guardian_found ? '✅' : '❌'}</td>
                          <td className="px-2 py-1 text-center">{s.title_matched ? '🔗' : '➕'}</td>
                          <td className="px-2 py-1 text-slate-600">{s.action || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            <div className="flex gap-2 justify-end pt-2 border-t border-slate-200">
              <button onClick={() => setReconcileReport(null)} disabled={reconcileRunning} className="px-4 py-2 rounded-lg border border-slate-200 text-sm font-semibold hover:bg-slate-50 disabled:opacity-50" data-testid="cancel-reconcile">Fechar</button>
              {reconcileReport.dry_run && (reconcileReport.would_create_title > 0 || reconcileReport.would_link_existing_title > 0 || reconcileReport.would_link_student > 0 || reconcileReport.would_link_responsible > 0) && (
                <button onClick={() => runReconcile(false)} disabled={reconcileRunning} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold disabled:opacity-50 inline-flex items-center gap-2" data-testid="apply-reconcile-btn">
                  {reconcileRunning ? <RefreshCw className="w-4 h-4 animate-spin"/> : <Link2 className="w-4 h-4"/>}
                  Aplicar reconcile
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
